import { useCallback, useEffect, useRef, useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import "../components/AppLayout.css";
import { clearFournisseurSession } from "../utils/auth";
import { getSupplierEngagementSubmissions } from "../api/api";

const LOGO_SRC = "/logoamicale.png";

export default function FournisseurLayout() {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [openNotifications, setOpenNotifications] = useState(false);
  const [, setReadVersion] = useState(0);
  const dropdownRef = useRef(null);

  const readKey = "fournisseur_read_notifications";
  let readIds = new Set();
  try {
    readIds = new Set(JSON.parse(localStorage.getItem(readKey) || "[]"));
  } catch (e) {
    readIds = new Set();
  }

  const loadNotifications = useCallback(async () => {
    try {
      const res = await getSupplierEngagementSubmissions();
      setRequests(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      // Supplier auth interceptor handles expired sessions.
    }
  }, []);

  useEffect(() => {
    loadNotifications();
    const interval = window.setInterval(loadNotifications, 5000);
    const refreshOnFocus = () => loadNotifications();
    window.addEventListener("focus", refreshOnFocus);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", refreshOnFocus);
    };
  }, [loadNotifications]);

  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenNotifications(false);
      }
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, []);

  const supplierNotifications = requests
    .filter((item) => item.supplier_status === "pending")
    .slice(0, 12);
  const unreadCount = supplierNotifications.filter((item) => !readIds.has(item.id)).length;

  const markSupplierNotificationsRead = () => {
    const ids = supplierNotifications.map((item) => item.id);
    localStorage.setItem(readKey, JSON.stringify(ids));
    setReadVersion((value) => value + 1);
    setOpenNotifications(false);
  };

  const handleLogout = () => {
    clearFournisseurSession();
    navigate("/fournisseur/login", { replace: true });
  };

  return (
    <main className="supplier-shell">
      <header className="supplier-header">
        <div className="supplier-brand">
          <img src={LOGO_SRC} alt="Logo Amicale Autoroutes" />
          <div>
            <span>Portail fournisseur</span>
            <h1>Espace fournisseur</h1>
          </div>
        </div>

        <nav className="supplier-nav">
          <div className="app-notifications" ref={dropdownRef}>
            <button
              type="button"
              className={`app-notification-button ${unreadCount > 0 ? "has-unread" : ""}`}
              onClick={() => setOpenNotifications((value) => !value)}
              aria-label="Notifications fournisseur"
            >
              <BellIcon />
              {unreadCount > 0 && <span>{unreadCount > 99 ? "99+" : unreadCount}</span>}
            </button>

            {openNotifications && (
              <div className="app-notification-panel">
                <div className="app-notification-panel__header">
                  <div>
                    <strong>Notifications fournisseur</strong>
                    <span>{unreadCount} nouvelle{unreadCount > 1 ? "s" : ""} demande{unreadCount > 1 ? "s" : ""}</span>
                  </div>
                  <button type="button" onClick={markSupplierNotificationsRead} disabled={supplierNotifications.length === 0}>
                    Tout lu
                  </button>
                </div>
                <div className="app-notification-list">
                  {supplierNotifications.length === 0 ? (
                    <div className="app-notification-empty">Aucune demande fournisseur en attente.</div>
                  ) : (
                    supplierNotifications.map((item) => (
                      <button
                        type="button"
                        key={item.id}
                        className={`app-notification-item ${readIds.has(item.id) ? "is-read" : ""}`}
                        onClick={() => {
                          setOpenNotifications(false);
                          navigate("/fournisseur/dashboard");
                        }}
                      >
                        <span className="app-notification-dot type-engagement" />
                        <span>
                          <strong>Nouvelle demande engagement</strong>
                          <small>{item.engagement_title || "Demande a traiter"}</small>
                          <em>{item.adherent_full_name || item.adherent_matricule || "Adherent"}</em>
                        </span>
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
          <NavLink to="/fournisseur/dashboard">Dashboard</NavLink>
          <NavLink to="/fournisseur/change-password">Mot de passe</NavLink>
          <button type="button" onClick={handleLogout}>Deconnexion</button>
        </nav>
      </header>

      <section className="supplier-content">
        <Outlet />
      </section>
    </main>
  );
}

function BellIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M18 9.8V11c0 2.2.6 3.5 1.4 4.5.5.6.1 1.5-.7 1.5H5.3c-.8 0-1.2-.9-.7-1.5C5.4 14.5 6 13.2 6 11V9.8C6 6.5 8.4 4 12 4s6 2.5 6 5.8Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      />
      <path
        d="M9.8 19a2.4 2.4 0 0 0 4.4 0"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="1.8"
      />
    </svg>
  );
}
